
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import moment from '../../utility/moment-timezone';
import { IMeateryBox } from '../../interfaces/meateryBox';
import { toFixedThree } from '../../utility/helper';

const MeateryBoxDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [box, setBox] = useState<IMeateryBox | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        fetchBox();
    }, [id]);

    const fetchBox = async () => {
        try {
            setLoading(true);
            const res = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}bundles/${id}`,
                { method: 'GET' }
            );
            setBox(res);
        } catch (err) {
            console.error('Failed to fetch meatery box', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Layout>Loading...</Layout>;
    }

    if (!box) {
        return <Layout>Box not found</Layout>;
    }

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="app-page-title">{box.title}</h1>
                <button
                    className="btn app-btn-secondary"
                    onClick={() => navigate(-1)}
                >
                    ← Back
                </button>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <img
                        src={box.images?.[0]}
                        alt={box.title}
                        className="img-fluid rounded"
                    />
                </div>

                <div className="col-md-8">
                    <table className="table table-sm">
                        <tbody>
                            <tr>
                                <th>Slug</th>
                                <td>{box.slug}</td>
                            </tr>
                            <tr>
                                <th>Status</th>
                                <td>
                                    <span
                                        className={`badge ${box.isActive
                                            ? 'bg-success'
                                            : 'bg-danger'
                                            }`}
                                    >
                                        {box.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Base Price</th>
                                <td>BHD {toFixedThree(box.price)}</td>
                            </tr>
                            <tr>
                                <th>Items Count</th>
                                <td>{box.items.length}</td>
                            </tr>
                            <tr>
                                <th>Created</th>
                                <td>
                                    {moment(box.createdAt).format(
                                        'DD MMM YYYY, hh:mm A'
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="app-card shadow-sm mb-4">
                <div className="app-card-body">
                    <h5>Description</h5>
                    <p className="mb-0">{box.description || '—'}</p>
                </div>
                <div className="app-card-body mb-4">
                    <h5>Additional Items</h5>
                    <p className="mb-0">{box.additionalItems || '—'}</p>
                </div>
            </div>
            <div className="app-card shadow-sm">
                <div className="app-card-body">
                    <h5>Products in this Box</h5>

                    <table className="table table-bordered align-middle">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Product</th>
                                <th>Quantity</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            {box.items.map((item) => (
                                <tr key={item.id}>
                                    <td style={{ width: 70 }}>
                                        <img
                                            src={
                                                item.product.images?.[0]
                                            }
                                            alt={item.product.title}
                                            style={{
                                                width: 50,
                                                height: 50,
                                                objectFit: 'cover',
                                            }}
                                        />
                                    </td>
                                    <td>{item.product.title}</td>
                                    <td>{item.quantity}</td>
                                    <td>
                                        <span
                                            className={`badge ${item.required
                                                ? 'bg-primary'
                                                : 'bg-secondary'
                                                }`}
                                        >
                                            {item.required
                                                ? 'Required'
                                                : 'Optional'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default MeateryBoxDetails;

